import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, OnInit } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { UnsubscribeController } from '@personal-area/global/common'
import { I18nService } from '@personal-area/global/i18n';
import { ApiFormService, Validators } from '@personal-area/global/form-validation/services';
import { ErrorTexts } from '@personal-area/global/form-validation/models';
import { UserApiService } from '@personal-area/global/users/services';
import { ProfileTrackingService } from '@personal-area/global/profile/services';
import {
  AlertModule,
  FILE_TYPE,
  FileAttachControlModule,
  FileAttachSettings,
  FormFieldComponent,
  CalendarInputComponent,
  ButtonComponent,
} from '@personal-area/global/ui';
import { LoaderModule } from '@personal-area/global/http/ui';
import { MIME_TYPES } from '@personal-area/global/http/models';
import { WebsocketFallbackService } from '@personal-area/global/websocket';
import { UserFacade } from '@personal-area/global/users/services';
import { LetDirective } from '@ngrx/component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Moment } from 'moment';
import moment from 'moment/moment';
import { first, forkJoin, map, switchMap, takeUntil } from 'rxjs';
import {
  SkipData,
  SkipVerificationDialogComponent,
} from '../../../components/skip-verification-dialog/skip-verification-dialog.component';
import { IdentityVerificationRequest } from '../../../models/identity-verification-request';
import { IDVerificationStatus } from '../../../models/verification-profile-response';
import { IdentityVerificationApiService } from '../../../services/identity-verification-api.service';
import { IdentityVerificationFacade } from '../../../state/facades/identity';
import { SummaryVerificationFacade } from '../../../state/facades/summary';

const MIN_YEARS_OLD = 18;
const BYTES_IN_A_HALF_MB = 524288;

@Component({
  templateUrl: './identity-manual.component.html',
  styleUrls: ['identity-manual.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ButtonComponent,
    CommonModule,
    TranslateModule,
    FormFieldComponent,
    FileAttachControlModule,
    RouterModule,
    CalendarInputComponent,
    ReactiveFormsModule,
    LoaderModule,
    LetDirective,
    AlertModule,
  ],
})
export class IdentityManualComponent extends UnsubscribeController implements OnInit {
  public readonly minDate = new Date(1900, 0, 1);
  public readonly maxDate: Date;
  public readonly fileAttachSettings: FileAttachSettings = {
    allowedContentTypes: [
      MIME_TYPES.jpg,
      MIME_TYPES.png,
      MIME_TYPES.bmp,
      MIME_TYPES.pdf,
    ],
    allowedFileSize: BYTES_IN_A_HALF_MB,
    maxFiles: 3,
    dimensions: undefined,
    customFileParameters: {
      type: MIME_TYPES.pdf,
      maxSize: BYTES_IN_A_HALF_MB,
    },
  };
  public readonly imageFileTypes = this.fileAttachSettings.allowedContentTypes
    .filter(type => type !== MIME_TYPES.pdf)
    .map(type => FILE_TYPE[type])
    .join(', ');
  public readonly documentFileTypes = FILE_TYPE[MIME_TYPES.pdf];
  protected readonly statuses = IDVerificationStatus;
  public readonly fieldErrors: Record<string, ErrorTexts> = {
    name: {
      availableChars: 'common.error.chars-are-not-available',
      required: 'profile-identity.error.name-required',
      maxlength: 'profile-identity.error.name-maxlength',
    },
    passport: {
      availableChars: 'common.error.chars-are-not-available',
      required: 'profile-identity.error.passport-required',
      maxlength: 'profile-identity.error.passport-maxlength',
      pattern: 'profile-identity.error.passport-invalid',
    },
    birthDate: {
      required: 'profile-identity.error.birthDate-required',
      birthdate: 'profile-identity.error.birthDate-invalid',
    },
    files: {
      required: 'profile-identity.error.files-required',
    },
  };
  public readonly verificationForm = new UntypedFormGroup({
    passport: new UntypedFormControl('', [
      Validators.required,
      Validators.availableChars,
      Validators.pattern('^[a-zA-Z 0-9-/.()]+$'),
    ]),
    birthDate: new UntypedFormControl(null, [Validators.required]),
    files: new UntypedFormControl('', [Validators.required]),
    name: new UntypedFormControl('', [
      Validators.required,
      Validators.availableChars,
      Validators.maxLength(255),
    ]),
  });
  public rejectReason$ = this._identityVerificationFacade.rejectReason$;
  public idStatus$ = this._summaryFacade.summary$.pipe(
    map(({ idStatus }) => idStatus),
  );
  public isLoading = false;
  public submitErrors = new EventEmitter<void>();

  constructor(
    private _formService: ApiFormService,
    private _userApiService: UserApiService,
    private _wsFallbackService: WebsocketFallbackService,
    private _identityVerificationService: IdentityVerificationApiService,
    private _identityVerificationFacade: IdentityVerificationFacade,
    private _summaryFacade: SummaryVerificationFacade,
    private _userFacade: UserFacade,
    private _profileTrackingService: ProfileTrackingService,
    private _router: Router,
    private _dialog: MatDialog,
    private _i18nService: I18nService,
    private _translateService: TranslateService,
  ) {
    super();

    const now = new Date();
    this.maxDate = new Date(now.setFullYear(now.getFullYear() - MIN_YEARS_OLD));
  }

  public ngOnInit(): void {
    this._userFacade.user$
      .pipe(first())
      .subscribe((user) => {
        this.verificationForm.setValue({
          passport: '',
          birthDate: user.birthDate
            ? moment(user.birthDate).utcOffset(0, true)
            : null,
          files: '',
          name: user.name,
        });
      });

    this._translateService.onLangChange
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(() => {
        this._identityVerificationFacade.getRequest();
      });
  }

  public normalizedBirthDate(birthDate: Moment): string {
    const localLocale = birthDate as Moment;
    return localLocale.locale('en').utcOffset(0, true).format();
  }

  public passportControlBlur(ctx: { focus: boolean, control: AbstractControl, field: string }): void {
    ctx.focus = false;
    this._trackPassportInputEvents(ctx.control);
  }

  public trackDateInputEvent(): void {
    this._profileTrackingService.birthDateInputChanged();
  }

  public trackUploadDocumentTapped(): void {
    this._profileTrackingService.uploadDocumentTapped();
  }

  public trackAddingFileStart(): void {
    this._profileTrackingService.uploadDocumentStart();
  }

  public trackAddingFileEnd(): void {
    this._profileTrackingService.uploadDocumentEnd();
  }

  public onSubmit(): void {
    if (!this.isLoading && this.verificationForm.valid) {
      this.isLoading = true;

      this._submitForm();
    } else {
      this.submitErrors.next();
    }
  }

  public promptSkip(): void {
    this._dialog.open(SkipVerificationDialogComponent, <MatDialogConfig<SkipData>>{
      panelClass: 'regular-popup-container',
      direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
      data: {
        text: 'verification-modal.text.skip.full',
      },
    });
  }

  private _trackPassportInputEvents(control: AbstractControl): void {
    this._profileTrackingService.passwordInputChanged();

    if (control.invalid) {
      this._profileTrackingService.passportInputError();
    }
  }

  private _submitForm(): void {
    this.isLoading = true;
    const data = this.verificationForm.value;

    const request: IdentityVerificationRequest = {
      passport: data.passport,
      birthDate: this.normalizedBirthDate(data.birthDate),
      files: data.files.length ? data.files : [data.files],
    };

    this._userFacade.user$
      .pipe(
        first(),
        switchMap(({ id }) =>
          forkJoin([
            this._userApiService.changeName(id, data.name),
            this._identityVerificationService.requestVerify(id, request),
          ]),
        ),
      )
      .subscribe({
        next: async () => {
          this.isLoading = false;
          this._wsFallbackService.reloadUser();
          this._summaryFacade.getSummary();
          await this._router.navigateByUrl('/dashboard');
        },
        error: (err) => {
          this.isLoading = false;
          this._formService.defaultFormError(err, this.verificationForm);
          this.submitErrors.next();
          this._sendPassportServerError(err);
        },
      });
  }

  private _sendPassportServerError(err: HttpErrorResponse): void {
    const ex = err.error?.exception || err.error?.mapException;
    if (ex?.errors?.passport) {
      this._profileTrackingService.passportInputError();
    }
  }
}
